import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { getTranslation } from "common";
import { useMonitor, usePriorities, useSelectedOrganization, useTranslations } from "hooks";
import ProcessItem, { StartedProcess } from "./ProcessItem";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { getUserData, SortCriterion } from "common/utilities";
import { SortState } from "hooks/useSortableList";
import { ConfirmDialog, TaskDetailDialog } from "./dialogs";
import { useSnackbar } from "hooks";
import { processesApi } from "api";
import { TaskDetail } from "./TaskItem";
import { ProcessRowSkeleton } from "./ProcessTableSkeleton";
import NoMonitor from "image/NoMonitor.png";
import NoMonitorDark from "image/NoMonitor-dark.png";
import { BaseTask } from "model/activitiesModel";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import SlaActivitiesProvider from "./sla/slaActivitiesContext";
import { SlaLiveHeaderCells, SlaLiveRefreshOnChange } from "./sla/SlaLiveRowCells";

interface FilterChip {
  label: string;
  onDelete: () => void;
}

interface ProcessesListProps {
  title: string;
  processes: Array<StartedProcess>;
  filters: Record<string, any>;
  filterChips: FilterChip[];
  refresh: () => Promise<StartedProcess[] | undefined>;
  handleChangeSort: (key: string) => void;
  newSort: SortCriterion;
  filteredUserDataKeys: string[];
  history: boolean;
  totalCount: number;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  loading: boolean;
  /**
   * Sostituisce il titolo **e** il chip del conteggio (es. i KPI SLA dei
   * processi vivi, che hanno gia' il chip "Totale" e rendono da soli titolo e
   * conteggio quando lo SLA e' spento). Assente = titolo e conteggio di sempre.
   */
  headerTitle?: React.ReactNode;
  /**
   * SLA dell'organizzazione attivo (lista viva): aggiunge le colonne stato SLA,
   * tempo restante e scadenza, alimentate da `SlaActivitiesProvider`.
   */
  slaEnabled?: boolean;
  /** Cambia a ogni rilettura della lista viva: rilegge le istanze SLA. */
  slaRefreshKey?: number;
}
const ProcessesList: React.FC<ProcessesListProps> = ({
  processes = [],
  title,
  filters,
  filterChips,
  refresh,
  handleChangeSort,
  newSort,
  filteredUserDataKeys,
  history,
  totalCount,
  loadMore,
  isLoadingMore,
  loading,
  headerTitle,
  slaEnabled = false,
  slaRefreshKey = 0,
}) => {
  const translations = useTranslations();
  const theme = useTheme();
  const noMonitorImage = theme.palette.mode === "dark" ? NoMonitorDark : NoMonitor;
  const [selectedTask, setSelectedTask] = useState<BaseTask | null>(null);
  const selectedOrganization = useSelectedOrganization();
  const isAdmin = selectedOrganization?.administrator;
  const [allPriorities, loadingPriorities] = usePriorities(selectedOrganization?.id || null);

  const [confirmData, setConfirmData] = useState<null | {
    task: TaskDetail;
    type: "delete" | "terminate" | "disclose" | "reschedule";
  }>(null);
  const [hasDate, setHasDate] = useState<boolean>(false);
  const {
    monitors,
    isLoading: isMonitorsLoading,
    getMonitors,
  } = useMonitor(selectedOrganization?.id);

  // ── Infinite scroll ──────────────────────────────────────────────────────────
  // Refs always hold the latest values so handlers never capture stale closures.
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  // Single observer for the life of the component. Do NOT recreate it when
  // isLoadingMore/hasMore change: a brand-new observer reports an element that
  // is already inside the rootMargin zone as "intersecting", so re-creating it
  // after every page load re-fires immediately and cascades through all the
  // remaining pages in one go. With a stable observer the callback only runs on
  // real intersection transitions, so the next page loads only after the user
  // actually scrolls the sentinel back into view. loadMore() already guards on
  // hasMore + loadingMore (via loadMoreRef, which always holds the latest
  // closure), so no extra gating is needed here.
  const observerRef = useRef<IntersectionObserver>();
  const sentinelCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '300px' },
    );
    observerRef.current.observe(node);
  }, []);

  // Flash-highlight the rows that arrived in the last loadMore batch once the
  // overlay lifts, so it's clear which records are new. Only pure appends
  // (prev ids ⊆ current ids) flash; filter/sort/refresh resets the baseline.
  const [newRowIds, setNewRowIds] = useState<Set<number>>(new Set());
  const prevRowIdsRef = useRef<Set<number> | null>(null);
  useEffect(() => {
    const ids = new Set(processes.map((p) => p.idprocess));
    const prev = prevRowIdsRef.current;
    if (prev && prev.size > 0 && Array.from(prev).every((id) => ids.has(id))) {
      const freshIds = processes
        .filter((p) => !prev.has(p.idprocess))
        .map((p) => p.idprocess);
      prevRowIdsRef.current = ids;
      if (!freshIds.length) return;
      setNewRowIds((cur) => new Set(Array.from(cur).concat(freshIds)));
      const t = setTimeout(() => setNewRowIds(new Set()), 2000);
      return () => clearTimeout(t);
    }
    prevRowIdsRef.current = ids;
    setNewRowIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processes]);

  const SKELETON_ROWS = 8;
  const slaColumns = slaEnabled && !history;
  // Righe visibili per il provider SLA: i token mai visti fanno rileggere le istanze.
  const slaVisibleTasks = useMemo(
    () =>
      slaColumns
        ? processes.map((p) => ({
            idprocess: p.idprocess,
            designerid: p.designerid || null,
            idprocesstoken: p.idprocesstoken,
          }))
        : [],
    [processes, slaColumns],
  );
  // ─────────────────────────────────────────────────────────────────────────────

  const { sendSnack } = useSnackbar();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);



  useEffect(() => {
    if (confirmData?.type === "reschedule") {
      const taskTimer = monitors.find(
        (timer) => timer.idprocesstoken === confirmData?.task.idprocesstoken
      );
      if (taskTimer) {
        setSelectedDate(new Date(taskTimer.starttime));
        setHasDate(true);
      }
    }
  }, [confirmData, monitors]);


  useEffect(() => {
    if (confirmData?.type === "reschedule") {
      const taskTimer = monitors.find(
        (timer) => timer.idprocesstoken === confirmData?.task.idprocesstoken
      );
      if (taskTimer) {
        // console.log(taskTimer.starttime);
        setSelectedDate(new Date(taskTimer.starttime));
        setHasDate(true);
      }
    }
  }, [confirmData, monitors]);

  const handleActionConfirm = async () => {
    try {
      if (confirmData?.type === "terminate") {
        const { data } = await processesApi.post(
          `/${confirmData?.task.idprocess}/terminate`
        );
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } else if (confirmData?.type === "delete") {
        const { data } = await processesApi.delete(
          `/${confirmData?.task.idprocess}`
        );
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } else if (confirmData?.type === "disclose") {
        const { data } = await processesApi.post(
          `/${confirmData?.task.idprocesstoken}/intermediateevents/terminate`
        );
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } else if (confirmData?.type === "reschedule") {
        const now = new Date();
        if (!selectedDate || selectedDate < now) {
          sendSnack({
            message: getTranslation(
              translations,
              "error.date_past.snackbar.text"
            ),
            type: "error",
          });
          return;
        }
        const UTCDate = selectedDate.toISOString();
        const { data } = await processesApi.post(
          `/${confirmData?.task.idprocesstoken}/intermediateevents/reorganize`,
          { date: UTCDate }
        );
        getMonitors();
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      }
      refresh();
    } catch (error) {
    } finally {
      setConfirmData(null);
    }
  };

  const fromDoingTo_Todo = async ({
    idprocesstoken,
  }: {
    idprocesstoken: number;
  }) => {
    try {
      await processesApi.post(`/${idprocesstoken}/undo`);
    } catch {
    } finally {
    }
  };

  const task = processes.find(
    (a) => a.idprocesstoken === selectedTask?.idprocesstoken
  ) as StartedProcess;

  return (
    <React.Fragment>
      <TaskDetailDialog
        readonly={!isAdmin || history}
        historyMode={history}
        administrator={isAdmin}
        handleBackStep={
          selectedTask?.state === 1 && isAdmin
            ? (task) => {
              fromDoingTo_Todo(task);
              setSelectedTask(null);
              refresh();
            }
            : undefined
        }
        open={Boolean(selectedTask)}
        onClose={() => setSelectedTask(null)}
        doing
        refresh={async () => {
          const newProcesses = await refresh();
          setSelectedTask((_process) => {
            const task =
              newProcesses?.find(
                (a) => _process?.idprocesstoken === a.idprocesstoken
              ) || null;
            return task;
          });
        }}
        task={{
          ...task,
          userdataLength: Object.keys(getUserData(task?.process_userdata))
            .length,
          userschema: task?.userschema,
          idprocessmodel: task?.process_idprocessmodel,
          idorganization: selectedOrganization?.id || 0,
          description: task?.process_description,
          descriptiondetail: task?.descriptiondetail ?? null,
          assignmentmode: 0,
          idswimlane: 0,
          designerid: "",
          userdata: task?.process_userdata,
          jsontag: JSON.stringify(task?.jsontag),
          priority_name: task?.priorityname || "",
          jsonparticipant: "",
          countcomments: 0,
          countattachments: 0,
          countuserdatafields: Object.keys(getUserData(task?.process_userdata)).length
        }}
      />
      <ConfirmDialog
        open={confirmData?.type === "terminate"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        content={getTranslation(translations, "general.terminate.text")}
      />
      <ConfirmDialog
        open={confirmData?.type === "delete"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        content={getTranslation(translations, "general.delete.text")}
      />
      <ConfirmDialog
        open={confirmData?.type === "disclose"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        content={getTranslation(translations, "general.disclose.text")}
      />
      <ConfirmDialog
        open={confirmData?.type === "reschedule"}
        onClose={() => setConfirmData(null)}
        onConfirm={handleActionConfirm}
        cancelText={getTranslation(translations, "generic.button.cancel")}
        confirmText={getTranslation(translations, "generic.button.confirm")}
        title={getTranslation(translations, "generic.button.confirm")}
        hasDate={hasDate}
        type="reschedule"
        content={
          <DateTimePicker
            value={dayjs(selectedDate)}
            label={getTranslation(translations, "general.date_and_time")}
            onChange={(v) => {
              if (v) {
                setSelectedDate(v.toDate());
                setHasDate(true);
              }
            }}
            slotProps={{
              textField: { size: "small" },
              popper: {
                placement: "bottom",
                modifiers: [
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      altAxis: true,
                      altBoundary: true,
                      tether: true,
                      rootBoundary: true,
                      padding: 8,
                    },
                  },
                  {
                    name: "flip",
                    enabled: true,
                    options: {
                      altBoundary: true,
                      rootBoundary: "document",
                      padding: 8
                    },
                  },
                ],
              },
            }}
            sx={{
              width: 250,
              mt: 1.5
            }}
          />
        }
      />
      <MaybeSlaProvider
        enabled={slaColumns}
        idorganization={selectedOrganization?.id}
        tasks={slaVisibleTasks}
        refreshKey={slaRefreshKey}
      >
      <Box sx={{ flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          sx={{ flexShrink: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {headerTitle ?? (
              <>
                <Typography variant="h6">{title}</Typography>
                <Chip
                  label={totalCount}
                  sx={{ height: 30, fontSize: 15, fontWeight: 700 }}
                />
              </>
            )}
            {filterChips.map((chip, index) => (
              <Chip
                key={index}
                label={chip.label}
                onDelete={chip.onDelete}
                sx={{ fontSize: 14, fontWeight: 700 }}
              />
            ))}
          </Stack>
        </Stack>
        <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, minHeight: 0, height: "100%", overflow: "auto" }}>
            {processes.length <= 0 && !loading ? (
              <Container
                sx={{
                  display: "flex",
                  height: "100%",
                  flexDirection: "column",
                  gap: 2,
                  justifyContent: "center",
                  alignItems: "center",
                }}
                maxWidth="md"
              >
                <Box
                  component="img"
                  height="50dvh"
                  width="auito"
                  sx={{ borderRadius: 9999 }}
                  src={noMonitorImage}
                  alt="no scheduled process"
                />
                <Typography fontWeight="bold">
                  {getTranslation(translations, "generic.nomonitor.message")}
                </Typography>
              </Container>
            ) : (
              <Box>
                <Table stickyHeader>
                  <TableHead
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        borderBottom: "none",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? theme.palette.grey[900] : "#eeeeee",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell
                        sx={{
                          width: "15%",
                          position: "sticky",
                          left: 0,
                          zIndex: 999,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? theme.palette.grey[900] : "#eeeeee",
                        }}
                        id="process-header"
                      >
                        <Stack flexDirection="row" spacing={1} alignItems="center">
                          <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.process")}</Typography>
                          <IconButton sx={{ opacity: newSort.model ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("model")}>
                            {newSort.model === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                          </IconButton>
                        </Stack>
                      </TableCell>
                      {filteredUserDataKeys.map((key) => (
                        <TableCell key={key}>
                          <Stack flexDirection="row" spacing={1} alignItems="center">
                            <Tooltip title={key}>
                              <Typography sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }} fontWeight="bold">{key}</Typography>
                            </Tooltip>
                            <IconButton sx={{ opacity: newSort[key] ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort(key)}>
                              {newSort[key] === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      ))}
                      {!history && (
                        <TableCell sx={{ width: "15%" }}>
                          <Stack flexDirection="row" spacing={1} alignItems="center">
                            <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.task")}</Typography>
                            <IconButton sx={{ opacity: newSort.activity ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("activity")}>
                              {newSort.activity === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      )}
                      <TableCell sx={{ width: "15%" }}>
                        <Stack flexDirection="row" spacing={1} alignItems="center">
                          <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.process.duration")}</Typography>
                          <IconButton sx={{ opacity: newSort.processduration ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("processduration")}>
                            {newSort.processduration === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                          </IconButton>
                        </Stack>
                      </TableCell>
                      {!history && (
                        <TableCell sx={{ width: "15%" }}>
                          <Stack flexDirection="row" spacing={1} alignItems="center">
                            <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.waiting_time.caption")}</Typography>
                            <IconButton sx={{ opacity: newSort.timetotakeover ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("timetotakeover")}>
                              {newSort.timetotakeover === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      )}
                      {!history && (
                        <TableCell sx={{ width: "15%" }}>
                          <Stack flexDirection="row" spacing={1} alignItems="center">
                            <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.working_time.caption")}</Typography>
                            <IconButton sx={{ opacity: newSort.timeindoing ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("timeindoing")}>
                              {newSort.timeindoing === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      )}
                      <TableCell sx={{ width: "10%" }}>
                        <Stack flexDirection="row" spacing={1} alignItems="center">
                          <Typography style={{ fontWeight: "bold" }}>{getTranslation(translations, "generic.priorities.caption")}</Typography>
                          <IconButton sx={{ opacity: newSort.priorityorder ? 1 : 0.3, mt: "0px !important" }} size="small" onClick={() => handleChangeSort("priorityorder")}>
                            {newSort.priorityorder === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                          </IconButton>
                        </Stack>
                      </TableCell>
                      {!history && (
                        <TableCell sx={{ width: "10%" }}>
                          {getTranslation(translations, "pm.label.process_status")}
                        </TableCell>
                      )}
                      {slaColumns && <SlaLiveHeaderCells />}
                      {!history && (
                        <TableCell sx={{ width: "20%" }}>
                          {getTranslation(translations, "generic.participants")}
                        </TableCell>
                      )}
                      {!history && (
                        <TableCell sx={{ width: "5%" }}>
                          {getTranslation(translations, "generic.action.title")}
                        </TableCell>
                      )}
                      {history && (
                        <TableCell sx={{ width: "5%" }}>
                          {getTranslation(translations, "generic.collapse.title")}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading
                      ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                        <ProcessRowSkeleton
                          key={`skeleton-${index}`}
                          history={history}
                          userDataKeysCount={filteredUserDataKeys.length}
                          slaColumns={slaColumns}
                        />
                      ))
                      : processes.map((process) => (
                        <ProcessItem
                          //key must me unique !!!!
                          key={`${process.idprocess}-${process.idprocesstoken}`}
                          setSelectedTask={setSelectedTask}
                          handleClick={(task, type) => setConfirmData({ task, type })}
                          monitors={monitors}
                          isMonitorsLoading={isMonitorsLoading}
                          filters={filters}
                          process={process}
                          userDataKeys={filteredUserDataKeys}
                          refresh={refresh}
                          allPriorities={allPriorities}
                          loadingPriorities={loadingPriorities}
                          history={history}
                          isNew={newRowIds.has(process.idprocess)}
                          slaColumns={slaColumns}
                        />
                      ))}
                  </TableBody>
                </Table>
                {/* Sentinel: IntersectionObserver target that triggers the next page.
                Works for both views now that the table is non-virtualized —
                when the table grows tall enough that the sentinel enters the
                viewport (the Dashboard Box is the actual scroll container),
                loadMore() fires. */}
                <Box ref={sentinelCallbackRef} sx={{ minHeight: 8 }} />
              </Box>
            )}
          </Box>
          {/* Load-more overlay: obscures the list while the next page is fetching so
          it's obvious when new records are about to be mounted. */}
          {isLoadingMore && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 99999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(18, 18, 18, 0.55)"
                    : "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(2px)",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        </Box>
      </Box>
      </MaybeSlaProvider>
    </React.Fragment>
  );
};

/**
 * Tetto delle pagine di istanze SLA lette per la lista viva (50 per pagina): il
 * Server non filtra per processo, e la sola prima pagina (ordinata per
 * scadenza) lasciava senza stato SLA i processi piu' recenti.
 */
const SLA_LIVE_INSTANCES_MAX_PAGES = 10;

/**
 * Provider SLA solo per la lista viva con SLA attivo: altrove (storico, SLA
 * spento) nessuna lettura delle istanze e nessun orologio al secondo.
 */
const MaybeSlaProvider: React.FC<{
  enabled: boolean;
  idorganization: number | undefined;
  tasks: Array<{ idprocess: number; designerid: string | null; idprocesstoken: number }>;
  refreshKey: number;
  children: React.ReactNode;
}> = ({ enabled, idorganization, tasks, refreshKey, children }) => {
  if (!enabled || !idorganization) return <>{children}</>;
  return (
    <SlaActivitiesProvider
      key={idorganization}
      idorganization={idorganization}
      tasks={tasks}
      maxPages={SLA_LIVE_INSTANCES_MAX_PAGES}
    >
      <SlaLiveRefreshOnChange refreshKey={refreshKey} />
      {children}
    </SlaActivitiesProvider>
  );
};

export default ProcessesList;
